import { useMemo } from "react";
import { IStatus } from "entities/RCA/StatusEntity/StatusEntityInterfaces";
import { useTranslation } from "react-i18next";
import { useRCAHistoryMainStyles } from "pages/Corporate/RCA/RCA.styles";
import {
    RCADueDateLog,
    RCAUserChangeLog,
    RCAStatusChangeLog,
    RCATextChangeLog,
    RCAHistoryLog,
    ChangeTypeEnums,
    FieldNameEnumsRCA,
    RCAUserHistoric,
} from "pages/Corporate/RCA/components/RCAHistoryLogsComponents";
import { IHistoryLogRCA } from "entities/RCA/RCALibraryEntity/RCALibraryEntityInterfaces";

export const RCALibraryHistoryLogItem = ({ log, statuses }: { log: IHistoryLogRCA; statuses: IStatus[] }) => {
    const classes = useRCAHistoryMainStyles();
    const { t } = useTranslation();

    const changeLog = useMemo(() => {
        if (log.field_name === FieldNameEnumsRCA.DUE_DATE || log.field_name === FieldNameEnumsRCA.INCIDENT_DATE) {
            return <RCADueDateLog newValue={log.new_value} oldValue={log.old_value} />;
        } else if (log.field_name === FieldNameEnumsRCA.OWNER_ID) {
            return <RCAUserChangeLog newValue={log.new_value} oldValue={log.old_value} />;
        } else if (log.field_name === FieldNameEnumsRCA.STATUS_ID) {
            const oldStatus = statuses.find((status) => status.status_id === Number(log.old_value));
            const newStatus = statuses.find((status) => status.status_id === Number(log.new_value));
            return <RCAStatusChangeLog oldStatus={oldStatus} newStatus={newStatus} />;
        } else if (
            log.field_name === FieldNameEnumsRCA.INCIDENT_DESCRIPTION ||
            log.field_name === FieldNameEnumsRCA.FIVE_WHY_ANALYSIS_PROBLEM_STATEMENT ||
            log.field_name === FieldNameEnumsRCA.FIVE_WHY_ANALYSIS_ROOT_CAUSE_CONCLUSION ||
            log.field_name === FieldNameEnumsRCA.WHY_ANSWER_TEXT ||
            log.field_name === FieldNameEnumsRCA.WHY_QUESTION_TEXT ||
            log.field_name === FieldNameEnumsRCA.FISHBONE_DIAGRAM_PROBLEM_STATEMENT ||
            log.field_name === FieldNameEnumsRCA.FISHBONE_DIAGRAM_PROBLEM_CAUSE_SECONDARY_ITEM_DESCRIPTION ||
            log.field_name === FieldNameEnumsRCA.FISHBONE_DIAGRAM_PROBLEM_CAUSE_MAIN_ITEM_DESCRIPTION
        ) {
            return <RCATextChangeLog newValue={log.new_value} oldValue={log.old_value} />;
        } else if (log.field_name === FieldNameEnumsRCA.MILL_ID) {
            return (
                <div className={classes.historyChangeWrapper}>
                    <span className={classes.historyText}>{t("rca.history_audit_logs.mill_reassignment")}</span>
                </div>
            );
        } else if (
            log.field_name === FieldNameEnumsRCA.ACTION_PLAN_CREATED_AT ||
            log.field_name === FieldNameEnumsRCA.ACTION_PLAN_DELETED_AT
        ) {
            return (
                <div className={classes.historyChangeWrapper}>
                    <span className={classes.historyText}>
                        {t("rca.history_audit_logs.action_plan_id", {
                            id: `-${log?.action_plan?.display_number}` || "",
                        })}
                    </span>
                </div>
            );
        } else if (
            log.field_name === FieldNameEnumsRCA.FISHBONE_DIAGRAM_PROBLEM_CAUSE_MAIN_ITEM_IS_HIGHLIGHTED ||
            log.field_name === FieldNameEnumsRCA.FISHBONE_DIAGRAM_PROBLEM_CAUSE_SECONDARY_ITEM_IS_HIGHLIGHTED
        ) {
            return (
                <div className={classes.historyChangeWrapper}>
                    <span className={classes.historyText}>
                        {log.new_value === "true"
                            ? t("rca.history_audit_logs.highlighted_item")
                            : t("rca.history_audit_logs.unhighlighted_item")}
                    </span>
                </div>
            );
        } else if (log.field_name === FieldNameEnumsRCA.RCA_DETAILS_FAILURE_CODE_ID) {
            return (
                <div className={classes.historyChangeWrapper}>
                    <span className={classes.historyText}>{t("rca.history_audit_logs.rca_details.failure_codes")}</span>
                </div>
            );
        } else if (log.field_name === FieldNameEnumsRCA.RCA_DETAILS_FAILURE_TYPE_ID) {
            return (
                <div className={classes.historyChangeWrapper}>
                    <span className={classes.historyText}>{t("rca.history_audit_logs.rca_details.failure_types")}</span>
                </div>
            );
        } else if (log.field_name === FieldNameEnumsRCA.RCA_DETAILS_FUNCTIONAL_LOCATION_ID) {
            return (
                <div className={classes.historyChangeWrapper}>
                    <span className={classes.historyText}>
                        {t("rca.history_audit_logs.rca_details.functional_location")}
                    </span>
                </div>
            );
        } else if (log.field_name === FieldNameEnumsRCA.RCA_DETAILS_MACHINE_CENTER_ID) {
            return (
                <div className={classes.historyChangeWrapper}>
                    <span className={classes.historyText}>
                        {t("rca.history_audit_logs.rca_details.machine_center")}
                    </span>
                </div>
            );
        } else if (log.field_name === FieldNameEnumsRCA.RCA_DETAILS_PRODUCTION_LINE_ID) {
            return (
                <div className={classes.historyChangeWrapper}>
                    <span className={classes.historyText}>
                        {t("rca.history_audit_logs.rca_details.production_line")}
                    </span>
                </div>
            );
        } else if (log.field_name === FieldNameEnumsRCA.RCA_DETAILS_SECONDARY_FAILURE_CODE_ID) {
            return (
                <div className={classes.historyChangeWrapper}>
                    <span className={classes.historyText}>
                        {t("rca.history_audit_logs.rca_details.secondary_failure_codes")}
                    </span>
                </div>
            );
        } else if (log.field_name === FieldNameEnumsRCA.RCA_DETAILS_SECONDARY_FUNCTIONAL_LOCATION_ID) {
            return (
                <div className={classes.historyChangeWrapper}>
                    <span className={classes.historyText}>
                        {t("rca.history_audit_logs.rca_details.secondary_functional_location")}
                    </span>
                </div>
            );
        } else if (log.field_name === FieldNameEnumsRCA.RCA_DETAILS_SHIFT_ID) {
            return (
                <div className={classes.historyChangeWrapper}>
                    <span className={classes.historyText}>{t("rca.history_audit_logs.rca_details.shift")}</span>
                </div>
            );
        } else if (
            log.field_name === FieldNameEnumsRCA.RCA_TEAM_MEMBER_USER_ID ||
            log.field_name === FieldNameEnumsRCA.RCA_TEAM_MEMBER_DELETED_AT
        ) {
            return (
                <div className={classes.historyChangeWrapper}>
                    <RCAUserHistoric userId={Number(log.new_value)} />
                </div>
            );
        }
    }, [classes, log, statuses, t]);

    const changedField = useMemo(() => {
        if (log.field_name === FieldNameEnumsRCA.DUE_DATE) {
            return t("rca.history_audit_logs.due_date");
        } else if (log.field_name === FieldNameEnumsRCA.INCIDENT_DATE) {
            return t("rca.history_audit_logs.incident_date");
        } else if (log.field_name === FieldNameEnumsRCA.OWNER_ID) {
            return t("rca.history_audit_logs.owner");
        } else if (log.field_name === FieldNameEnumsRCA.STATUS_ID) {
            return t("rca.history_audit_logs.status");
        } else if (log.field_name === FieldNameEnumsRCA.INCIDENT_DESCRIPTION) {
            return t("rca.history_audit_logs.description");
        } else if (log.field_name === FieldNameEnumsRCA.MILL_ID) {
            return t("rca.history_audit_logs.mill");
        } else if (
            log.field_name === FieldNameEnumsRCA.ACTION_PLAN_CREATED_AT ||
            log.field_name === FieldNameEnumsRCA.ACTION_PLAN_DELETED_AT
        ) {
            return `${t("rca.history_audit_logs.action_plan")}`;
        } else if (
            log.field_name === FieldNameEnumsRCA.WHY_QUESTION_TEXT ||
            log.field_name === FieldNameEnumsRCA.WHY_QUESTION_DELETED_AT
        ) {
            return `${t("rca.history_audit_logs.five_why_analysis")}. ${t("rca.history_audit_logs.question")}`;
        } else if (log.field_name === FieldNameEnumsRCA.FIVE_WHY_ANALYSIS_PROBLEM_STATEMENT) {
            return `${t("rca.history_audit_logs.five_why_analysis")}. ${t(
                "rca.rca_library.rca_detail.5_whys_analysis.problem_statement",
            )}`;
        } else if (log.field_name === FieldNameEnumsRCA.FIVE_WHY_ANALYSIS_ROOT_CAUSE_CONCLUSION) {
            return `${t("rca.history_audit_logs.five_why_analysis")}. ${t(
                "rca.rca_library.rca_detail.5_whys_analysis.root_cause_conclusion",
            )}`;
        } else if (
            log.field_name === FieldNameEnumsRCA.WHY_ANSWER_TEXT ||
            log.field_name === FieldNameEnumsRCA.WHY_ANSWER_DELETED_AT
        ) {
            return `${t("rca.history_audit_logs.five_why_analysis")}. ${t("rca.history_audit_logs.answer")}`;
        } else if (log.field_name === FieldNameEnumsRCA.FISHBONE_DIAGRAM_PROBLEM_STATEMENT) {
            return `${t("rca.history_audit_logs.fishbone_diagram")}. ${t("rca.history_audit_logs.problem")}`;
        } else if (
            log.field_name === FieldNameEnumsRCA.FISHBONE_DIAGRAM_PROBLEM_CAUSE_MAIN_ITEM_DESCRIPTION ||
            log.field_name === FieldNameEnumsRCA.FISHBONE_DIAGRAM_PROBLEM_CAUSE_MAIN_ITEM_DELETED_AT ||
            log.field_name === FieldNameEnumsRCA.FISHBONE_DIAGRAM_PROBLEM_CAUSE_MAIN_ITEM_IS_HIGHLIGHTED
        ) {
            return `${t("rca.history_audit_logs.fishbone_diagram")}. ${t("rca.history_audit_logs.main_item")}`;
        } else if (
            log.field_name === FieldNameEnumsRCA.FISHBONE_DIAGRAM_PROBLEM_CAUSE_SECONDARY_ITEM_DELETED_AT ||
            log.field_name === FieldNameEnumsRCA.FISHBONE_DIAGRAM_PROBLEM_CAUSE_SECONDARY_ITEM_IS_HIGHLIGHTED ||
            log.field_name === FieldNameEnumsRCA.FISHBONE_DIAGRAM_PROBLEM_CAUSE_SECONDARY_ITEM_DESCRIPTION
        ) {
            return `${t("rca.history_audit_logs.fishbone_diagram")}. ${t("rca.history_audit_logs.secondary_item")}`;
        } else if (
            log.field_name === FieldNameEnumsRCA.RCA_DETAILS_FAILURE_CODE_ID ||
            log.field_name === FieldNameEnumsRCA.RCA_DETAILS_FAILURE_TYPE_ID ||
            log.field_name === FieldNameEnumsRCA.RCA_DETAILS_FUNCTIONAL_LOCATION_ID ||
            log.field_name === FieldNameEnumsRCA.RCA_DETAILS_MACHINE_CENTER_ID ||
            log.field_name === FieldNameEnumsRCA.RCA_DETAILS_PRODUCTION_LINE_ID ||
            log.field_name === FieldNameEnumsRCA.RCA_DETAILS_SECONDARY_FAILURE_CODE_ID ||
            log.field_name === FieldNameEnumsRCA.RCA_DETAILS_SECONDARY_FUNCTIONAL_LOCATION_ID ||
            log.field_name === FieldNameEnumsRCA.RCA_DETAILS_SHIFT_ID
        ) {
            return `${t("rca.history_audit_logs.rca_details.title")}`;
        } else if (
            log.field_name === FieldNameEnumsRCA.RCA_TEAM_MEMBER_DELETED_AT ||
            log.field_name === FieldNameEnumsRCA.RCA_TEAM_MEMBER_USER_ID
        ) {
            return `${t("rca.history_audit_logs.rca_team_members")}`;
        } else if (log.field_name === FieldNameEnumsRCA.CREATED_AT || log.field_name === FieldNameEnumsRCA.DELETED_AT) {
            return t("rca.rca_dashboard.rca");
        } else if (log.field_name === FieldNameEnumsRCA.FIVE_WHY_ANALYSIS_MEDIA_ID) {
            return `${t("rca.history_audit_logs.five_why_analysis")}. ${t("rca.history_audit_logs.media")}`;
        } else if (
            log.field_name === FieldNameEnumsRCA.RCA_MEDIA_CREATED_AT ||
            log.field_name === FieldNameEnumsRCA.RCA_MEDIA_DELETED_AT
        ) {
            return t("rca.history_audit_logs.media");
        } else if (log.field_name === FieldNameEnumsRCA.MEETING_TYPE_ID) {
            return t("rca.history_audit_logs.meeting_type");
        }
        return "";
    }, [log, t]);

    const changeType = useMemo(() => {
        if (ChangeTypeEnums.INSERT === log.change_type) {
            return (
                <>
                    {t("rca.history_audit_logs.creation")} <strong>{changedField}</strong>
                </>
            );
        } else if (ChangeTypeEnums.UPDATE === log.change_type) {
            return (
                <>
                    {t("rca.history_audit_logs.updated_field")} <strong>{changedField}</strong>
                </>
            );
        } else if (ChangeTypeEnums.DELETE === log.change_type) {
            return (
                <>
                    {t("rca.history_audit_logs.deletion")} <strong>{changedField}</strong>
                </>
            );
        }
    }, [log, changedField, t]);

    return (
        <RCAHistoryLog
            changeType={changeType}
            changeLog={changeLog}
            changeDate={log.changed_at}
            changeUser={log.user}
        />
    );
};
